import styled from "@emotion/styled";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {
  ClickAwayListener,
  List,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { setIsSidebarOpen } from "src/shared/lib/store/slices/app";
import { useGetSidebarMenuLiksQuery } from "src/shared/api/sideMenuLinks/sideMenuLinks";
import { ISidebarLinkProps } from "src/shared/api/sideMenuLinks/types";
import { Loader, WebcomLogo } from "src/shared/components";
import { routes } from "src/shared/constants/routes.constants";
import { useAuth } from "src/shared/lib/rback/AuthContext";
import { useTypedDispatch, useTypedSelector } from "src/shared/lib/store/store";
import { EIconNames } from "src/shared/types/iconNames";
import { ExpandButton } from "./ExpandButton";
import { SidebarLink } from "./SidebarLink";

const StyledAside = styled("aside")(
  ({ theme }) => `
    background-color: #3d485a;
    color: white;
    font-weight: 300;
    height: 100vh;
    left: 0;
    position: fixed;
    top: 0;
    z-index: 3;
    scrollbar-width: thin;
    scrollbar-color: #8f8f8f transparent;

    ${theme.breakpoints.up("lg")} {
      &.aside {
        &--opened {
          ~ header, ~ main {
            margin-left: 268px;
          }
        }
      }
    }
    & .rotated {
      transform: rotate(180deg);
    }
`,
);

const StyledNav = styled("nav")(
  ({ theme }) => `
    height: 100%;
    opacity: 0;
    overflow-x: hidden;
    padding-top: 96px;
    position: relative;
    width: 20px;
    display: flex;
    flex-direction: column;
    transition: opacity .1s ease-in-out, max-width .2s ease-in-out, width .2s ease-in-out;

    ${theme.breakpoints.down("lg")} {
      width: 0;
    }

    &.open, &:hover {
      opacity: 1;
      width: 268px;
    }
`,
);

const StyledAnchor = styled("a")(`
  svg{
    width: 168px;
    height: 35px;
  }

  left: 50%;
  overflow: hidden;
  position: absolute;
  top: 24px;
  transform: translateX(-50%);
  padding: 2px;
  display: inline-block;
  text-decoration: none;
  outline: none;
  transition: opacity 0.2s ease-in-out, color 0.2s ease-in-out, background-color 0.2s ease-in-out;
`);

const StyledList = styled(List)(`
  margin: 0;
  padding: 0;
  overflow-y: auto;
  flex-grow: 1;
  min-height: 1px;
  scrollbar-width: thin;
  scrollbar-color: #8f8f8f transparent;
`);

export const Aside = () => {
  const { data, isLoading } = useGetSidebarMenuLiksQuery(undefined);
  const { hasPermission } = useAuth();

  const dispatch = useTypedDispatch();
  const isOpen = useTypedSelector((store) => store.app.isSidebarOpen);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const menu = data?.menu;
  const notice = data?.notice;

  const toggleSidebar = () => {
    dispatch(setIsSidebarOpen(!isOpen));
  };

  const modifiedSideBarLinks = useMemo(() => {
    if (!data || !menu) return [];
    //TODO: убрать костыль ниже
    const clonedData2 = JSON.parse(JSON.stringify(menu)) as ISidebarLinkProps[];

    const clonedData = [
      ...clonedData2.filter((item) => item.service_name !== "partner-reports"),
    ];

    const crmLink = clonedData.find((link) => link.service_name === "crm");

    const transactionsChildLink: ISidebarLinkProps = {
      to: "",
      service_name: "partners",
      uri: routes.transactions,
      text: "Транзакции",
      requiredpermissions: "transactions.view",
    };

    const contextLink = clonedData.find(
      (link) => link.service_name === "context",
    );

    if (contextLink) {
      const contextChildren = contextLink.children ?? [];

      const alreadyExists = contextChildren.some(
        (child) => child.uri === routes.transactions,
      );

      if (!alreadyExists) {
        const postpaidIndex = contextChildren.findIndex(
          (child) => child.uri === "/context2/checkout/postpaid",
        );

        if (postpaidIndex >= 0) {
          contextChildren.splice(postpaidIndex + 1, 0, transactionsChildLink);
        } else {
          contextChildren.push(transactionsChildLink);
        }
      }

      contextLink.children = contextChildren;
    }

    if (crmLink?.children) {
      const partnersChild = crmLink.children.find(
        (child) =>
          child.uri === "/crm/desk/partners" && child.text === "Партнеры",
      );

      if (partnersChild) {
        partnersChild.requiredpermissions = ["crm.partners"];
      }
    }

    const prepareLinks = (links: ISidebarLinkProps[]): ISidebarLinkProps[] => {
      return links
        .map((link) => {
          const filteredChildren = link.children
            ?.filter((child) => {
              return (
                !child.requiredpermissions ||
                hasPermission(child.requiredpermissions)
              );
            })
            .map((child) => ({
              ...child,
              isExternal: child.isExternal || link.service_name === "roombook",
            }));

          const shouldInclude =
            !link.requiredpermissions ||
            hasPermission(link.requiredpermissions) ||
            (filteredChildren && filteredChildren.length > 0);

          if (!shouldInclude) return null;

          return {
            ...link,
            isExternal: true,
            count:
              link.service_name === "mailbox"
                ? notice?.mailbox?.count
                : undefined,
            counters:
              link.service_name === "finances" && notice?.finances
                ? [
                    {
                      key: "webcom_by",
                      value: notice.finances.webcom_by,
                      color: "#eb4545",
                    },
                    {
                      key: "webcom_ru",
                      value: notice.finances.webcom_ru,
                      color: "#db59bc",
                    },
                    {
                      key: "icontext",
                      value: notice.finances.icontext,
                      color: "#844de5",
                    },
                    {
                      key: "seo",
                      value: notice.finances.seo,
                      color: "#0a7ca3",
                    },
                  ]
                : undefined,
            children: filteredChildren,
          };
        })
        .filter(Boolean) as ISidebarLinkProps[];
    };

    const preparedLinks = prepareLinks(clonedData);

    const partnersLinks: ISidebarLinkProps = {
      to: "",
      service_name: "partners",
      uri: routes.partners,
      text: "Партнеры",
      icon_type: EIconNames.PERSON_OLD,
      requiredpermissions: ["partners.view", "partners.view.team"],
    };

    const reportsChildren: ISidebarLinkProps[] = [
      {
        service_name: "partner_reports",
        uri: routes.draftReport,
        text: "Предварительный отчет",
        icon_type: EIconNames.PERSON_OLD,
        to: "",
        requiredpermissions: "partners.draft-report",
      },
      {
        service_name: "partner_reports",
        uri: routes.summaryReport,
        text: "Сводный отчет",
        icon_type: EIconNames.PERSON_OLD,
        to: "",
        requiredpermissions: "partners.summary-report",
      },
      {
        service_name: "partner_reports",
        uri: routes.finalReport,
        text: "Итоговый отчет",
        icon_type: EIconNames.PERSON_OLD,
        to: "",
        requiredpermissions: "partners.final-report",
      },
      {
        service_name: "partner_reports",
        uri: routes.partnersDocDept,
        text: "Задолженность партнеров",
        icon_type: EIconNames.PERSON_OLD,
        to: "",
        requiredpermissions: "crm:partners:doc_debt",
      },
    ].filter((link) => hasPermission(link.requiredpermissions));

    const reportsLinks: ISidebarLinkProps | null =
      reportsChildren.length > 0
        ? {
            to: "",
            service_name: "partner_reports",
            uri: routes.draftReport,
            text: "Партнерские отчеты",
            icon_type: EIconNames.TWO_PERSON,
            children: reportsChildren,
          }
        : null;

    const finalLinks = [...preparedLinks];

    if (hasPermission(partnersLinks.requiredpermissions!)) {
      finalLinks.splice(3, 0, partnersLinks);
    }

    if (reportsLinks) {
      finalLinks.splice(4, 0, reportsLinks);
    }

    return finalLinks;
  }, [data, menu, hasPermission, notice?.mailbox?.count, notice?.finances]);

  useEffect(() => {
    return () => {
      if (isMobile) {
        dispatch(setIsSidebarOpen(false));
      }
    };
  }, [isMobile, dispatch]);

  if (isLoading) {
    return <Loader position="center" />;
  }

  return (
    <ClickAwayListener
      onClickAway={(e) => {
        const target = e.target as HTMLElement;
        const isBurger = Boolean(target.closest("#burger"));

        if (!isMobile || isBurger) {
          return;
        }

        if (isOpen) {
          toggleSidebar();
        }
      }}
    >
      <StyledAside className={isOpen ? "aside--opened" : undefined}>
        <ExpandButton onClick={toggleSidebar}>
          <ChevronRightRoundedIcon className={isOpen ? "rotated" : undefined} />
        </ExpandButton>

        <StyledNav className={isOpen ? "open" : undefined}>
          <StyledAnchor href="/">
            <WebcomLogo />
          </StyledAnchor>

          <StyledList>
            {modifiedSideBarLinks?.map((link) => (
              <SidebarLink
                key={String(link.uri || link.service_name)}
                {...link}
              />
            ))}
          </StyledList>
        </StyledNav>
      </StyledAside>
    </ClickAwayListener>
  );
};
